import { ref } from 'vue';
import { language } from '.';

const GRAMMAR_PROGRESS_STORAGE_KEY = 'grammarProgress';

export interface GrammarChapterProgress {
  completed: boolean;
}

export interface GrammarProgress {
  completedChapters: number;
  totalChapters: number;
  overallProgress: number;
  chapters: Record<string, GrammarChapterProgress>;
}

const DEFAULT_PROGRESS = (): GrammarProgress => ({
  completedChapters: 0,
  totalChapters: 0,
  overallProgress: 0,
  chapters: {}
});

export default () => {
  const { langForeign, langNative, langLevel } = language.state;

  const grammarProgress = ref<GrammarProgress>(DEFAULT_PROGRESS());
  const grammarProgressLangs = ref<Record<string, GrammarProgress>>({});

  const getLangKey = () => `${langForeign.value}_${langNative.value}_${langLevel.value}`;

  const saveToLocalStorage = () => {
    try {
      grammarProgressLangs.value[getLangKey()] = grammarProgress.value;
      localStorage.setItem(
        GRAMMAR_PROGRESS_STORAGE_KEY,
        JSON.stringify(grammarProgressLangs.value)
      );
    } catch (error) {
      console.error('Failed to save grammar progress to localStorage:', error);
    }
  };

  const loadFromLocalStorage = () => {
    const stored = localStorage.getItem(GRAMMAR_PROGRESS_STORAGE_KEY);
    if (stored) {
      grammarProgressLangs.value = JSON.parse(stored);
      const key = getLangKey();
      grammarProgress.value = grammarProgressLangs.value[key]
        ? grammarProgressLangs.value[key]
        : DEFAULT_PROGRESS();
      return true;
    }
    return false;
  };

  const initGrammarProgressState = (allChapterNumbers: string[]) => {
    loadFromLocalStorage();
    // Ensure all chapters are present in state (new chapters may have been added)
    for (const chapterNumber of allChapterNumbers) {
      if (!grammarProgress.value.chapters[chapterNumber]) {
        grammarProgress.value.chapters[chapterNumber] = { completed: false };
      }
    }
    grammarProgress.value.totalChapters = allChapterNumbers.length;
    recalculate();
  };

  const recalculate = () => {
    const chapters = Object.values(grammarProgress.value.chapters);
    const completed = chapters.filter((c) => c.completed).length;
    grammarProgress.value.completedChapters = completed;
    grammarProgress.value.overallProgress =
      chapters.length > 0 ? Math.round((completed / chapters.length) * 100) : 0;
  };

  const markChapterCompleted = (chapterNumber: string) => {
    if (!grammarProgress.value.chapters[chapterNumber]) {
      grammarProgress.value.chapters[chapterNumber] = { completed: false };
    }
    grammarProgress.value.chapters[chapterNumber].completed = true;
    recalculate();
    saveToLocalStorage();
  };

  const isChapterCompleted = (chapterNumber: string): boolean => {
    return grammarProgress.value.chapters[chapterNumber]?.completed ?? false;
  };

  const resetProgress = () => {
    grammarProgress.value = DEFAULT_PROGRESS();
    saveToLocalStorage();
  };

  return {
    state: { grammarProgress },
    funcs: {
      initGrammarProgressState,
      markChapterCompleted,
      isChapterCompleted,
      resetProgress,
      saveToLocalStorage,
      loadFromLocalStorage
    }
  };
};
